import { Injectable } from '@angular/core';
import { HttpService } from '../../core/http.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface MenuItem {
  id: number;
  title: string;
  url: string;
  parent: string;
  order: number;
  type: string;
  object_id: string;
  children?: MenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  constructor(private httpService: HttpService) { }

  /**
   * Get primary menu items from WordPress API
   */
  getPrimaryMenu(): Observable<any[]> {
    const url = (environment as any).youtubeApiUrl + 'menu/primary'
    return this.httpService.get<MenuItem[]>(url).pipe(
      map(response => this.processMenuItems(response))
    );
  }

  /**
   * Process menu items to create hierarchical structure and convert to existing format
   */
  private processMenuItems(items: MenuItem[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const menuMap = new Map<number, MenuItem>();
    const rootItems: MenuItem[] = [];

    // First pass: create map of all items
    items.forEach(item => {
      item.children = [];
      menuMap.set(item.id, item);
    });

    // Second pass: build hierarchy
    items.forEach(item => {
      if (item.parent === '0') {
        rootItems.push(item);
      } else {
        const parentId = parseInt(item.parent);
        const parent = menuMap.get(parentId);
        if (parent) {
          parent.children!.push(item);
        }
      }
    });

    // Third pass: sort all levels by order
    this.sortMenuItems(rootItems);

    // Convert to existing menu format
    return this.convertToExistingFormat(rootItems);
  }

  /**
   * Recursively sort menu items by order
   */
  private sortMenuItems(items: MenuItem[]): void {
    items.sort((a, b) => a.order - b.order);
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        this.sortMenuItems(item.children);
      }
    });
  }

  /**
   * Convert WordPress menu items to existing menu format
   */
  private convertToExistingFormat(items: MenuItem[]): any[] {

    return items.map(item => {
      const menuItem: any = {
        title: item.title,
        routerLink: this.getRouterLink(item.url),
      };

      // Handle submenu
      if (item.children && item.children.length > 0) {
        if (item.title === 'Interviews') {
          // Special handling for Interviews menu to match existing structure
          menuItem.submenu = this.buildInterviewsSubmenu(item.children);
        } else {
          // General submenu handling
          menuItem.submenu = this.buildGeneralSubmenu(item.children);
        }
      }

      return menuItem;
    });
  }

  /**
   * Build general submenu structure
   */
  private buildGeneralSubmenu(children: MenuItem[]): any[] {
    return children.map(child => ({
      title: child.title,
      routerLink: this.getRouterLink(child.url),
      category: child.title,
      children: child.children ? this.buildSubmenuChildren(child.children) : []
    }));
  }

  /**
   * Build submenu children recursively
   */
  private buildSubmenuChildren(children: MenuItem[]): any[] {
    return children.map(child => ({
      title: child.title,
      routerLink: this.getRouterLink(child.url),
      slug: this.extractSlugFromUrl(child.url),
      category: child.title,
      children: child.children ? this.buildSubmenuChildren(child.children) : []
    }));
  }

  /**
   * Build interviews submenu to match existing structure
   */
  private buildInterviewsSubmenu(children: MenuItem[]): any[] {
    return children.map(child => ({
      category: child.title,
      title: child.title,
      routerLink: this.getRouterLink(child.url),
      children: child.children ? this.buildSubmenuChildren(child.children) : []
    }));
  }

  /**
   * Convert URL to router link with proper hierarchy support
   */
  private getRouterLink(url: string): string {

    if (url.startsWith('#')) {
      const hash = url.substring(1);
      switch (hash) {
        case 'about-us':
          return '/about-us';
        case 'interviews':
          return '/interviews/list';
        case 'ytd-channel':
          return '/youtube-channel';
        case 'test':
          return '/coming-soon';
        case 'url':
          return '/coming-soon';
        default:
          return '/coming-soon';
      }
    } else if (url.includes('api.superlawyer.in') || url.includes('test.superlawyer.in')) {
      // For category URLs, create hierarchical routes
      if (url.includes('/category/')) {
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/').filter(part => part);

          // Extract category hierarchy and create proper routes
          if (pathParts.length >= 2 && pathParts[0] === 'category') {
            const categoryPath = pathParts.slice(1); // Remove 'category'

            // Create interview route with full path for breadcrumbs
            if (categoryPath.length === 1) {
              // Single level category (e.g., international, legal-head-hunter)
              return `/interviews/${categoryPath[0]}`;
            } else if (categoryPath.length === 2) {
              // Two level category (e.g., national/supreme-court)
              return `/interviews/${categoryPath[0]}/${categoryPath[1]}`;
            } else if (categoryPath.length >= 3) {
              // Three or more levels (e.g., international/politics)
              return `/interviews/${categoryPath.join('/')}`;
            }
          }
        } catch (error) {
          console.warn('Error parsing category URL:', url);
          return '/interviews/list';
        }
      }

      // For home URL
      if (url === 'https://api.superlawyer.in/' || url === 'https://api.superlawyer.in') {
        return '/';
      }

      // Extract path from full URL
      try {
        const urlObj = new URL(url);
        return urlObj.pathname === '/' ? '/' : urlObj.pathname;
      } catch {
        return '/';
      }
    }
    return url.startsWith('/') ? url : '/' + url;
  }

  /**
   * Extract slug from URL for breadcrumb navigation
   */
  private extractSlugFromUrl(url: string): string {
    if (url.includes('/category/')) {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(part => part);

        // Return just the last part of the category path for subcategories
        if (pathParts.length >= 2 && pathParts[0] === 'category') {
          const categoryPath = pathParts.slice(1); // Remove 'category'
          return categoryPath[categoryPath.length - 1]; // Return just the subcategory name
        }
      } catch (error) {
        console.warn('Error parsing URL for slug:', url);
      }
    }

    // Fallback to title-based slug
    return url.replace(/[^a-zA-Z0-9\s-]/g, '').toLowerCase().replace(/\s+/g, '-');
  }
}
